import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { CaretLeft, CaretRight, Info } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOURS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

// Available weekdays: 3 = Wednesday, 6 = Saturday (Sunday = 0)
const AVAILABLE_WEEKDAYS = new Set([3, 6]);

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const buildGrid = (monthDate) => {
  // Monday-first grid (ISO). 6 rows of 7 cells.
  const first = startOfMonth(monthDate);
  // JS: 0 = Sunday. Convert to Monday-first index (Mon=0, ..., Sun=6)
  const isoFirstWeekday = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - isoFirstWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
};

export const BookingCalendar = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  // 48h+ booking buffer — earliest selectable day is today + 2 calendar days
  const earliest = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 2);
    return d;
  }, [today]);

  const [viewMonth, setViewMonth] = useState(startOfMonth(today));

  // Fetch booked slots once
  const [bookedByDate, setBookedByDate] = useState({});
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/availability`)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const b of res.data?.booked || []) {
          if (!b?.date || !b?.time) continue;
          map[b.date] = map[b.date] || new Set();
          map[b.date].add(b.time);
        }
        setBookedByDate(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isSlotBooked = (isoDate, time) => Boolean(bookedByDate[isoDate]?.has?.(time));
  const isDayFullyBooked = (isoDate) =>
    Boolean(bookedByDate[isoDate]) && bookedByDate[isoDate].size >= HOURS.length;

  const lang = i18n.language?.startsWith("de") ? "de-CH" : i18n.language?.startsWith("en") ? "en-GB" : "pt-PT";

  const monthLabel = useMemo(
    () =>
      viewMonth.toLocaleDateString(lang, { month: "long", year: "numeric" }).replace(/^./, (c) => c.toUpperCase()),
    [viewMonth, lang]
  );

  const cells = useMemo(() => buildGrid(viewMonth), [viewMonth]);

  // Build weekday labels (Mon..Sun) once per locale change.
  const weekdayLabels = useMemo(() => {
    // 2024-01-01 is a Monday — use it to derive locale-aware short names.
    const ref = new Date(2024, 0, 1);
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(ref);
      d.setDate(ref.getDate() + i);
      return d.toLocaleDateString(lang, { weekday: "short" }).replace(/\.$/, "");
    });
  }, [lang]);

  const isAvailable = (d) => {
    if (d < earliest) return false; // 48h buffer: today and tomorrow are blocked
    if (!AVAILABLE_WEEKDAYS.has(d.getDay())) return false;
    if (isDayFullyBooked(toISO(d))) return false;
    return true;
  };

  const selectedDate = value?.date ? new Date(value.date + "T00:00:00") : null;

  const setDate = (d) => {
    onChange({ date: toISO(d), time: value?.time && AVAILABLE_WEEKDAYS.has(d.getDay()) ? value.time : "" });
  };

  const setTime = (h) => {
    onChange({ ...(value || {}), time: h });
  };

  // Disable prev nav past current month
  const canPrev = startOfMonth(viewMonth) > startOfMonth(today);

  return (
    <div data-testid="booking-calendar" className="bg-white border border-[#E4E2DA] rounded-sm">
      {/* Month header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E2DA]">
        <button
          type="button"
          onClick={() => canPrev && setViewMonth((m) => addMonths(m, -1))}
          disabled={!canPrev}
          data-testid="cal-prev"
          aria-label="previous month"
          className={`w-8 h-8 rounded-full flex items-center justify-center border border-[#E4E2DA] transition-colors ${canPrev ? "hover:bg-[#F6F5F2] text-[#3E5239]" : "opacity-30 cursor-not-allowed text-[#9AA395]"}`}
        >
          <CaretLeft size={14} weight="bold" />
        </button>
        <div className="font-serif text-lg text-[#3E5239] font-light tracking-wide">{monthLabel}</div>
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          data-testid="cal-next"
          aria-label="next month"
          className="w-8 h-8 rounded-full flex items-center justify-center border border-[#E4E2DA] hover:bg-[#F6F5F2] text-[#3E5239] transition-colors"
        >
          <CaretRight size={14} weight="bold" />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 px-3 pt-3 text-center">
        {weekdayLabels.map((w) => (
          <div key={w} className="text-[10px] uppercase tracking-[0.18em] text-[#9AA395] font-semibold py-2">
            {w}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 px-3 pb-3 gap-0.5">
        {cells.map((d) => {
          const iso = toISO(d);
          const inMonth = d.getMonth() === viewMonth.getMonth();
          if (!inMonth) {
            return <div key={iso} className="aspect-square" aria-hidden="true" />;
          }

          const past = d < earliest;
          const available = isAvailable(d);
          const isSelected = selectedDate && sameDay(d, selectedDate);
          const isToday = sameDay(d, today);

          if (available) {
            const stateClasses = isSelected
              ? "bg-[#3E5239] text-white font-medium"
              : "text-[#3E5239] hover:bg-[#A7CA99]/25 border border-[#A7CA99]/50";
            const todayRing = isToday && !isSelected ? "ring-1 ring-[#A7CA99]" : "";
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setDate(d)}
                data-testid={`cal-day-${iso}`}
                className={`aspect-square flex items-center justify-center text-sm rounded-sm transition-all ${stateClasses} ${todayRing}`}
              >
                {d.getDate()}
              </button>
            );
          }

          // Unavailable / past — rendered as visibly "booked"
          const unavailableClasses = past
            ? "text-[#D8D5CC]"
            : "text-[#C7C3B8] line-through bg-[#F6F5F2]";
          return (
            <div
              key={iso}
              data-testid={`cal-day-unavailable-${iso}`}
              aria-disabled="true"
              className={`aspect-square flex items-center justify-center text-sm rounded-sm select-none ${unavailableClasses}`}
              title={past ? "" : t("booking.fully_booked", "Sem vagas")}
            >
              {d.getDate()}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="px-5 py-3 border-t border-[#E4E2DA] flex flex-wrap items-center gap-4 text-[11px] text-[#6B7A66] font-light">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 border border-[#A7CA99]/60 rounded-sm" />
          {t("booking.legend_available", "Disponível")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 bg-[#F6F5F2] border border-[#E4E2DA] rounded-sm">
            <span className="block w-full h-px bg-[#C7C3B8] mt-1" />
          </span>
          {t("booking.legend_full", "Sem vagas")}
        </span>
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div data-testid="time-slots" className="px-5 py-5 border-t border-[#E4E2DA] bg-[#FCFBF7]">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7A66] font-semibold mb-3">
            {t("booking.choose_time", "Escolha o horário")}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {HOURS.map((h) => {
              const active = value?.time === h;
              const booked = isSlotBooked(value.date, h);
              let slotClasses;
              if (booked) {
                slotClasses = "bg-[#F6F5F2] text-[#C7C3B8] border-[#E4E2DA] line-through cursor-not-allowed";
              } else if (active) {
                slotClasses = "bg-[#3E5239] text-white border-[#3E5239]";
              } else {
                slotClasses = "bg-white text-[#3E5239] border-[#E4E2DA] hover:border-[#3E5239]/60 hover:bg-[#A7CA99]/10";
              }
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => !booked && setTime(h)}
                  disabled={booked}
                  data-testid={`time-${h}`}
                  className={`px-3 py-2.5 text-sm rounded-sm border transition-all ${slotClasses}`}
                  title={booked ? t("booking.fully_booked", "Sem vagas") : ""}
                >
                  {h}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending confirmation notice */}
      <div className="px-5 py-4 border-t border-[#E4E2DA] bg-[#A7CA99]/10 flex items-start gap-3">
        <Info size={18} weight="bold" className="text-[#3E5239] shrink-0 mt-0.5" />
        <p className="text-xs text-[#3E5239] font-light leading-relaxed">
          {t(
            "booking.notice",
            "O horário escolhido é uma preferência. A nossa equipa confirmará a sua marcação por e-mail ou telefone em até 24 horas."
          )}
        </p>
      </div>
    </div>
  );
};
